import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LogOut, RefreshCw, Trash2, Save, ExternalLink } from "lucide-react";

const STATUSES = ["new", "contacted", "quoted", "closed"];
const STATUS_COLORS = {
  new: "bg-[#FF5A1F]/15 text-[#FF5A1F]",
  contacted: "bg-[#2563EB]/15 text-[#60A5FA]",
  quoted: "bg-[#10B981]/15 text-[#10B981]",
  closed: "bg-slate-500/15 text-slate-400",
};

function Field({ label, value, onChange, textarea, placeholder, testid }) {
  return (
    <div>
      <Label className="text-slate-300">{label}</Label>
      {textarea ? (
        <Textarea data-testid={testid} rows={3} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          className="mt-2 bg-[#0D0F12] border-white/10 text-white placeholder:text-slate-500" />
      ) : (
        <Input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          className="mt-2 bg-[#0D0F12] border-white/10 text-white placeholder:text-slate-500" />
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadEnquiries = useCallback(() => {
    api.get("/admin/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => {
        setUser(r.data);
        loadEnquiries();
        api.get("/content").then((c) => setContent(c.data)).catch(() => {});
      })
      .catch(() => navigate("/admin/login"));
  }, [navigate, loadEnquiries]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  const upd = (path, value) =>
    setContent((prev) => {
      const next = structuredClone(prev);
      const keys = path.split(".");
      let o = next;
      keys.slice(0, -1).forEach((k) => (o = o[k]));
      o[keys[keys.length - 1]] = value;
      return next;
    });

  const saveContent = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/content", content);
      setContent(data);
      toast.success("Website content updated — live immediately");
    } catch {
      toast.error("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { status });
      loadEnquiries();
      toast.success("Status updated");
    } catch {
      toast.error("Could not update status");
    }
  };

  const removeEnquiry = async (id) => {
    try {
      await api.delete(`/admin/enquiries/${id}`);
      loadEnquiries();
      toast.success("Enquiry deleted");
    } catch {
      toast.error("Could not delete enquiry");
    }
  };

  if (!user || !content)
    return (
      <div className="min-h-screen bg-[#0D0F12] flex items-center justify-center" data-testid="admin-loading">
        <div className="h-10 w-10 rounded-full border-2 border-[#FF5A1F] border-t-transparent animate-spin" />
      </div>
    );

  const newCount = enquiries.filter((e) => e.status === "new").length;

  return (
    <div className="min-h-screen bg-[#0D0F12] text-slate-100 font-body" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0D0F12]/85 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-white rounded-xl px-3 py-1.5">
              <img src="/logo.png" alt="A-Z Concepts UK Ltd" className="h-8 w-auto" />
            </div>
            <span className="hidden sm:block text-sm text-slate-400" data-testid="admin-user-email">{user.email}</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-view-site-link"
              className="flex items-center gap-1.5 text-sm text-slate-300 hover:text-[#FF5A1F] transition-colors">
              <ExternalLink size={15} /> View Site
            </Link>
            <button onClick={logout} data-testid="admin-logout-button"
              className="flex items-center gap-1.5 text-sm font-semibold border border-white/20 rounded-full px-4 py-2 hover:bg-white/10 transition-colors">
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight" data-testid="admin-heading">Website Manager</h1>
        <p className="mt-2 text-sm text-slate-400">
          {enquiries.length} enquiries total · <span className="text-[#FF5A1F] font-semibold">{newCount} new</span>
        </p>

        <Tabs defaultValue="enquiries" className="mt-8">
          <TabsList className="bg-[#161B22] border border-white/10" data-testid="admin-tabs">
            <TabsTrigger value="enquiries" data-testid="admin-tab-enquiries">Enquiries</TabsTrigger>
            <TabsTrigger value="content" data-testid="admin-tab-content">Site Content</TabsTrigger>
            <TabsTrigger value="seo" data-testid="admin-tab-seo">SEO Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="enquiries" className="mt-6">
            <div className="flex justify-end mb-4">
              <button onClick={loadEnquiries} data-testid="admin-refresh-enquiries-button"
                className="flex items-center gap-2 text-sm font-semibold border border-white/20 rounded-full px-4 py-2 hover:bg-white/10 transition-colors">
                <RefreshCw size={14} /> Refresh
              </button>
            </div>
            {enquiries.length === 0 ? (
              <div className="bg-[#161B22] border border-white/10 rounded-2xl p-12 text-center text-slate-400" data-testid="admin-enquiries-empty">
                No enquiries yet. New submissions from your website form will appear here and be emailed to you.
              </div>
            ) : (
              <div className="space-y-4" data-testid="admin-leads-table">
                {enquiries.map((enq) => (
                  <div key={enq.id} className="bg-[#161B22] border border-white/10 rounded-2xl p-6" data-testid={`admin-enquiry-card-${enq.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <p className="font-heading text-lg font-bold">{enq.name}
                          {enq.company && <span className="ml-2 text-sm font-normal text-slate-400">· {enq.company}</span>}
                        </p>
                        <p className="mt-1 text-sm text-slate-400">
                          <a href={`tel:${enq.phone}`} className="hover:text-[#FF5A1F]">{enq.phone}</a>
                          {" · "}
                          <a href={`mailto:${enq.email}`} className="hover:text-[#FF5A1F]">{enq.email}</a>
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-xs font-bold uppercase tracking-wider rounded-full px-3 py-1 ${STATUS_COLORS[enq.status] || STATUS_COLORS.new}`}
                          data-testid={`admin-enquiry-status-${enq.id}`}>{enq.status}</span>
                        <Select value={enq.status} onValueChange={(v) => setStatus(enq.id, v)}>
                          <SelectTrigger className="w-32 bg-[#0D0F12] border-white/10 text-white" data-testid={`admin-enquiry-status-select-${enq.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#161B22] border-white/10 text-white">
                            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <button onClick={() => removeEnquiry(enq.id)} data-testid={`admin-enquiry-delete-${enq.id}`}
                          className="p-2 text-slate-400 hover:text-red-400 transition-colors" aria-label="Delete enquiry">
                          <Trash2 size={17} />
                        </button>
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-400">
                      {enq.product_interest && <span>Interest: <span className="text-slate-200">{enq.product_interest}</span></span>}
                      {enq.quantity && <span>Qty: <span className="text-slate-200">{enq.quantity}</span></span>}
                      <span>{new Date(enq.created_at).toLocaleString()}</span>
                    </div>
                    {enq.message && <p className="mt-3 text-sm text-slate-300 bg-[#0D0F12] rounded-xl p-4 leading-relaxed">{enq.message}</p>}
                    {enq.attachment_path && (
                      <button
                        onClick={async () => {
                          try {
                            const r = await api.get(`/files/${enq.attachment_path}`, { responseType: "blob" });
                            window.open(URL.createObjectURL(r.data), "_blank");
                          } catch {
                            toast.error("Could not open the file");
                          }
                        }}
                        data-testid={`admin-enquiry-attachment-${enq.id}`}
                        className="mt-3 inline-flex items-center gap-2 text-sm font-bold text-[#60A5FA] hover:text-[#2563EB] transition-colors">
                        <ExternalLink size={15} /> View artwork: {enq.attachment_name || "attached file"}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="content" className="mt-6" data-testid="admin-content-editor-section">
            <div className="space-y-8">
              <section className="bg-[#161B22] border border-white/10 rounded-2xl p-8">
                <h2 className="font-heading text-xl font-bold">Hero Section</h2>
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Eyebrow label" value={content.hero.eyebrow} onChange={(v) => upd("hero.eyebrow", v)} testid="content-hero-eyebrow-input" />
                  <Field label="Hero image URL" value={content.hero.image} onChange={(v) => upd("hero.image", v)} testid="content-hero-image-input" />
                  <div className="sm:col-span-2"><Field label="Main headline" value={content.hero.title} onChange={(v) => upd("hero.title", v)} textarea testid="content-hero-title-input" /></div>
                  <div className="sm:col-span-2"><Field label="Sub-headline" value={content.hero.subtitle} onChange={(v) => upd("hero.subtitle", v)} textarea testid="content-hero-subtitle-input" /></div>
                  <div className="sm:col-span-2"><Field label="Factory video URL (optional .mp4 — plays in the Sublimation section)" value={content.hero.video_url} onChange={(v) => upd("hero.video_url", v)} placeholder="https://.../factory.mp4" testid="content-hero-video-input" /></div>
                </div>
              </section>

              <section className="bg-[#161B22] border border-white/10 rounded-2xl p-8">
                <h2 className="font-heading text-xl font-bold">Contact Details</h2>
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Phone (displayed & dialled)" value={content.contact.phone} onChange={(v) => upd("contact.phone", v)} testid="content-contact-phone-input" />
                  <Field label="WhatsApp number (digits only, with country code)" value={content.contact.whatsapp} onChange={(v) => upd("contact.whatsapp", v)} placeholder="447000000000" testid="content-contact-whatsapp-input" />
                  <Field label="Email" value={content.contact.email} onChange={(v) => upd("contact.email", v)} testid="content-contact-email-input" />
                  <Field label="Business hours" value={content.contact.hours} onChange={(v) => upd("contact.hours", v)} testid="content-contact-hours-input" />
                  <div className="sm:col-span-2"><Field label="Address" value={content.contact.address} onChange={(v) => upd("contact.address", v)} testid="content-contact-address-input" /></div>
                </div>
              </section>

              <section className="bg-[#161B22] border border-white/10 rounded-2xl p-8">
                <h2 className="font-heading text-xl font-bold">About Section</h2>
                <div className="mt-5 space-y-5">
                  <Field label="Heading" value={content.about.title} onChange={(v) => upd("about.title", v)} testid="content-about-title-input" />
                  <Field label="Text" value={content.about.text} onChange={(v) => upd("about.text", v)} textarea testid="content-about-text-input" />
                </div>
              </section>

              <section className="bg-[#161B22] border border-white/10 rounded-2xl p-8">
                <h2 className="font-heading text-xl font-bold">Products</h2>
                <p className="mt-1 text-sm text-slate-400">Edit names, descriptions, minimum order labels, photos and the SEO text for each product's own page.</p>
                <div className="mt-6 space-y-6">
                  {content.products.map((p, i) => (
                    <div key={p.id} className="border border-white/10 rounded-xl p-6" data-testid={`content-product-editor-${p.id}`}>
                      <p className="text-xs font-bold uppercase tracking-wider text-[#FF5A1F] mb-4">{p.name}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Name" value={p.name} onChange={(v) => upd(`products.${i}.name`, v)} testid={`content-product-name-${p.id}`} />
                        <Field label="MOQ label" value={p.moq} onChange={(v) => upd(`products.${i}.moq`, v)} testid={`content-product-moq-${p.id}`} />
                        <Field label="Tagline" value={p.tagline} onChange={(v) => upd(`products.${i}.tagline`, v)} testid={`content-product-tagline-${p.id}`} />
                        <Field label="Image URL" value={p.image} onChange={(v) => upd(`products.${i}.image`, v)} testid={`content-product-image-${p.id}`} />
                        <div className="sm:col-span-2"><Field label="Short description (homepage card)" value={p.description} onChange={(v) => upd(`products.${i}.description`, v)} textarea testid={`content-product-description-${p.id}`} /></div>
                        <div className="sm:col-span-2"><Field label="Long description (product page)" value={p.long_description || ""} onChange={(v) => upd(`products.${i}.long_description`, v)} textarea testid={`content-product-longdesc-${p.id}`} /></div>
                        <div className="sm:col-span-2">
                          <Field label="Features (comma separated)" value={p.features.join(", ")}
                            onChange={(v) => upd(`products.${i}.features`, v.split(",").map((s) => s.trim()).filter(Boolean))}
                            testid={`content-product-features-${p.id}`} />
                        </div>
                        <div className="sm:col-span-2">
                          <Field label="Specifications (comma separated)" value={(p.specs || []).join(", ")}
                            onChange={(v) => upd(`products.${i}.specs`, v.split(",").map((s) => s.trim()).filter(Boolean))}
                            testid={`content-product-specs-${p.id}`} />
                        </div>
                        <div className="sm:col-span-2">
                          <Field label="FAQs (one per line: Question | Answer)" textarea
                            value={(p.faqs || []).map((f) => `${f.q} | ${f.a}`).join("\n")}
                            onChange={(v) => upd(`products.${i}.faqs`, v.split("\n").map((l) => { const [q, ...r] = l.split("|"); return { q: (q || "").trim(), a: r.join("|").trim() }; }).filter((f) => f.q && f.a))}
                            testid={`content-product-faqs-${p.id}`} />
                        </div>
                        <Field label="SEO title (Google)" value={p.seo_title || ""} onChange={(v) => upd(`products.${i}.seo_title`, v)} testid={`content-product-seo-title-${p.id}`} />
                        <Field label="SEO description (Google)" value={p.seo_description || ""} onChange={(v) => upd(`products.${i}.seo_description`, v)} testid={`content-product-seo-desc-${p.id}`} />
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="bg-[#161B22] border border-white/10 rounded-2xl p-8">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <h2 className="font-heading text-xl font-bold">Work Gallery</h2>
                    <p className="mt-1 text-sm text-slate-400">Photos of past customer orders shown on your homepage. Replace these samples with your real job photos.</p>
                  </div>
                  <button onClick={() => setContent((prev) => ({ ...prev, gallery: [...(prev.gallery || []), { id: `photo-${Date.now()}`, image: "", title: "New order", caption: "" }] }))}
                    data-testid="gallery-add-button"
                    className="text-sm font-bold border border-white/20 rounded-full px-4 py-2 hover:bg-white/10 transition-colors">
                    + Add Photo
                  </button>
                </div>
                <div className="mt-6 space-y-6">
                  {(content.gallery || []).map((g, i) => (
                    <div key={g.id} className="border border-white/10 rounded-xl p-6" data-testid={`gallery-editor-${g.id}`}>
                      <div className="flex justify-between items-center mb-4">
                        <p className="text-xs font-bold uppercase tracking-wider text-[#FF5A1F]">Photo {i + 1}</p>
                        <button onClick={() => setContent((prev) => ({ ...prev, gallery: prev.gallery.filter((_, gi) => gi !== i) }))}
                          data-testid={`gallery-remove-${g.id}`} className="p-2 text-slate-400 hover:text-red-400 transition-colors" aria-label="Remove photo">
                          <Trash2 size={16} />
                        </button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Image URL" value={g.image} onChange={(v) => upd(`gallery.${i}.image`, v)} testid={`gallery-image-${g.id}`} />
                        <Field label="Title" value={g.title} onChange={(v) => upd(`gallery.${i}.title`, v)} testid={`gallery-title-${g.id}`} />
                        <div className="sm:col-span-2"><Field label="Caption" value={g.caption} onChange={(v) => upd(`gallery.${i}.caption`, v)} testid={`gallery-caption-${g.id}`} /></div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="bg-[#161B22] border border-white/10 rounded-2xl p-8">
                <h2 className="font-heading text-xl font-bold">Sublimation Page</h2>
                <p className="mt-1 text-sm text-slate-400">Content for your dedicated sublimation page (/sublimation). Example photos come from the Work Gallery above.</p>
                <div className="mt-5 space-y-5">
                  <Field label="Page headline" value={content.sublimation?.hero_title || ""} onChange={(v) => upd("sublimation.hero_title", v)} testid="sublimation-title-input" />
                  <Field label="Sub-headline" value={content.sublimation?.hero_subtitle || ""} onChange={(v) => upd("sublimation.hero_subtitle", v)} textarea testid="sublimation-subtitle-input" />
                  <Field label="Intro paragraph" value={content.sublimation?.intro || ""} onChange={(v) => upd("sublimation.intro", v)} textarea testid="sublimation-intro-input" />
                </div>
                <p className="mt-6 mb-3 text-xs font-bold uppercase tracking-wider text-[#FF5A1F]">Process steps</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {(content.sublimation?.steps || []).map((s, i) => (
                    <div key={i} className="border border-white/10 rounded-xl p-4" data-testid={`sublimation-step-editor-${i}`}>
                      <Field label={`Step ${i + 1} title`} value={s.title} onChange={(v) => upd(`sublimation.steps.${i}.title`, v)} testid={`sublimation-step-title-${i}`} />
                      <div className="mt-3">
                        <Field label={`Step ${i + 1} text`} value={s.text} onChange={(v) => upd(`sublimation.steps.${i}.text`, v)} textarea testid={`sublimation-step-text-${i}`} />
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="bg-[#161B22] border border-white/10 rounded-2xl p-8">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <h2 className="font-heading text-xl font-bold">Buyer Reviews</h2>
                    <p className="mt-1 text-sm text-slate-400">Testimonials shown just above the quote form. Replace these samples with real customer reviews.</p>
                  </div>
                  <button onClick={() => setContent((prev) => ({ ...prev, testimonials: [...(prev.testimonials || []), { id: `t-${Date.now()}`, name: "", company: "", rating: 5, text: "" }] }))}
                    data-testid="testimonial-add-button"
                    className="text-sm font-bold border border-white/20 rounded-full px-4 py-2 hover:bg-white/10 transition-colors">
                    + Add Review
                  </button>
                </div>
                <div className="mt-6 space-y-6">
                  {(content.testimonials || []).map((t, i) => (
                    <div key={t.id} className="border border-white/10 rounded-xl p-6" data-testid={`testimonial-editor-${t.id}`}>
                      <div className="flex justify-between items-center mb-4">
                        <p className="text-xs font-bold uppercase tracking-wider text-[#FF5A1F]">Review {i + 1}</p>
                        <button onClick={() => setContent((prev) => ({ ...prev, testimonials: prev.testimonials.filter((_, ti) => ti !== i) }))}
                          data-testid={`testimonial-remove-${t.id}`} className="p-2 text-slate-400 hover:text-red-400 transition-colors" aria-label="Remove review">
                          <Trash2 size={16} />
                        </button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Customer name" value={t.name} onChange={(v) => upd(`testimonials.${i}.name`, v)} testid={`testimonial-name-${t.id}`} />
                        <Field label="Company / role" value={t.company} onChange={(v) => upd(`testimonials.${i}.company`, v)} testid={`testimonial-company-${t.id}`} />
                        <Field label="Star rating (1–5)" value={String(t.rating ?? 5)} onChange={(v) => upd(`testimonials.${i}.rating`, Math.max(1, Math.min(5, parseInt(v) || 5)))} testid={`testimonial-rating-${t.id}`} />
                        <div className="sm:col-span-2"><Field label="Review text" value={t.text} onChange={(v) => upd(`testimonials.${i}.text`, v)} textarea testid={`testimonial-text-${t.id}`} /></div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <div className="sticky bottom-6 flex justify-end">
                <button onClick={saveContent} disabled={saving} data-testid="admin-save-content-button"
                  className="inline-flex items-center gap-2 bg-[#FF5A1F] hover:bg-[#E04810] disabled:opacity-60 text-white font-bold rounded-full px-8 py-3.5 shadow-lg shadow-black/40 transition-colors">
                  <Save size={17} /> {saving ? "Saving..." : "Save & Publish"}
                </button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="seo" className="mt-6" data-testid="admin-seo-settings-form">
            <section className="bg-[#161B22] border border-white/10 rounded-2xl p-8 max-w-3xl">
              <h2 className="font-heading text-xl font-bold">SEO Settings</h2>
              <p className="mt-1 text-sm text-slate-400">These control how azconcepts.net appears in Google search results.</p>
              <div className="mt-6 space-y-5">
                <Field label="Page title (50–60 characters is ideal)" value={content.seo.title} onChange={(v) => upd("seo.title", v)} testid="seo-title-input" />
                <Field label="Meta description (150–160 characters is ideal)" value={content.seo.description} onChange={(v) => upd("seo.description", v)} textarea testid="seo-description-input" />
                <Field label="Keywords (comma separated)" value={content.seo.keywords} onChange={(v) => upd("seo.keywords", v)} textarea testid="seo-keywords-input" />
              </div>
              <button onClick={saveContent} disabled={saving} data-testid="seo-save-button"
                className="mt-7 inline-flex items-center gap-2 bg-[#FF5A1F] hover:bg-[#E04810] disabled:opacity-60 text-white font-bold rounded-full px-8 py-3 transition-colors">
                <Save size={16} /> {saving ? "Saving..." : "Save SEO Settings"}
              </button>
            </section>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
